declare const process: { env: Record<string, string | undefined> };

function getApiBaseUrl(): string {
  if (typeof process !== 'undefined' && process.env.API_BASE_URL != null && process.env.API_BASE_URL !== '') {
    return process.env.API_BASE_URL;
  }
  if (typeof window !== 'undefined' && window.location != null) {
    return '';
  }
  throw new Error(
    'API_BASE_URL is required for server-side / edge API client. Set it in your environment.',
  );
}

type ApiClientConfig = {
  baseUrl?: string;
};

export function createApiClient(config?: ApiClientConfig): ApiClient {
  return new ApiClient(config?.baseUrl ?? API_BASE_URL);
}

const API_BASE_URL = getApiBaseUrl();

export interface ApiError extends Error {
  statusCode: number;
  code: string;
}

export class ApiClient {
  constructor(private readonly baseUrl: string = API_BASE_URL) {}

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    init?: RequestInit,
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const token = typeof window !== 'undefined' ? localStorage.getItem('xstore-access-token') : null;
    const botId = typeof window !== 'undefined' ? localStorage.getItem('xstore-selected-bot-id') : null;

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token != null && token !== '' ? { Authorization: `Bearer ${token}` } : {}),
        ...(botId != null && botId !== '' ? { 'X-Bot-Id': botId } : {}),
        ...(init?.headers ?? {}),
      },
      body: body != null ? JSON.stringify(body) : undefined,
      ...init,
    });

    if (!response.ok) {
      const data = (await response.json().catch(() => ({}))) as { message?: string; code?: string };
      const error = new Error(data.message ?? response.statusText) as ApiError;
      error.statusCode = response.status;
      error.code = data.code ?? 'UNKNOWN_ERROR';
      throw error;
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  get<T>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>('GET', path, undefined, init);
  }

  post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>('POST', path, body, init);
  }

  put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>('PUT', path, body, init);
  }

  delete<T>(path: string, init?: RequestInit): Promise<T> {
    return this.request<T>('DELETE', path, undefined, init);
  }
}

declare const __XSTORE_API_BASE_URL__: string | undefined;

export const apiClient = new ApiClient(
  typeof window !== 'undefined' && typeof __XSTORE_API_BASE_URL__ !== 'undefined'
    ? __XSTORE_API_BASE_URL__
    : API_BASE_URL,
);
